import { useMemo } from 'react';
import { FailureFilters } from '@/types/filters';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { TurbineForForecast, WeibullAnalysisResultWithMetadata } from '@/types/forecast';
import { FailureRecord } from '@/types/failure';
import { Turbine } from '@/types';
import { FORECAST_CALCULATION_CONSTANTS } from '@/constants/forecastCalculation';
import { validateSubset, createTurbineMap, applyTurbineFilters } from '@/utils/forecastCalculationHelpers';
import { transformTurbineToFailureRecord } from '@/utils'

interface PopulationAnalysisResult {
  populationSize: number;
  forecastSubsetSize: number;
  turbinesForForecast: TurbineForForecast[];
  validationErrors: string[];
}

interface UsePopulationAnalysisProps {
  turbineData?: { failures: FailureRecord[] } | null;
  populationTurbineData?: { turbines: Turbine[] } | null;
  sourceAnalysis: WeibullAnalysisResult | null;
  filters: FailureFilters;
}

export const usePopulationAnalysis = ({
  turbineData,
  populationTurbineData,
  sourceAnalysis,
  filters
}: UsePopulationAnalysisProps): PopulationAnalysisResult => {
  return useMemo(() => {
    if (!turbineData?.failures || !sourceAnalysis || !populationTurbineData?.turbines) {
      return {
        populationSize: 0,
        forecastSubsetSize: 0,
        turbinesForForecast: [],
        validationErrors: sourceAnalysis ? ['No turbine data available'] : ['No source analysis loaded']
      };
    }

    const failures = turbineData.failures;
    const turbineFailures: string[] = [];
    const turbinesForPopulation = populationTurbineData.turbines;

    // Go over turbine for population and if not in failures, transform and add.
    failures.forEach((f) => turbineFailures.push(`${f.site_name}:${f.turbine_name}`));
    turbinesForPopulation.forEach((t) => {
      if (!turbineFailures.includes(`${t.site_name}:${t.turbine_name}`)) {
        failures.push(transformTurbineToFailureRecord(t))
      }
    });

    const errors: string[] = [];

    // Get source analysis filters for subset validation
    const sourceAnalysisWithMetadata = sourceAnalysis as WeibullAnalysisResultWithMetadata;
    const sourceFilters = sourceAnalysisWithMetadata.metadata?.filtersUsed || {};

    // Validate that current filters are subsets of source analysis
    const siteError = validateSubset(filters.sites, sourceFilters.sites, 'Sites');
    const componentError = validateSubset(filters.componentsMajor, sourceFilters.componentsMajor, 'Components');
    const modelError = validateSubset(filters.turbineModels, sourceFilters.turbineModels, 'Turbine Models');

    if (siteError) errors.push(siteError);
    if (componentError) errors.push(componentError);
    if (modelError) errors.push(modelError);

    // Create turbine map
    const turbineMap = createTurbineMap(failures);
    const allTurbines = Array.from(turbineMap.values());

    // STEP 1: Get initial population matching source analysis filters
    const initialPopulation = applyTurbineFilters(allTurbines, sourceFilters, true);
    const initialPopulationSize = initialPopulation.length;

    // STEP 2: Filter initial population by current forecast filters to get subset
    const forecastTurbines = applyTurbineFilters(initialPopulation, filters, true);

    // Validation checks
    if (initialPopulationSize === 0) {
      errors.push('No turbines in initial Weibull analysis population');
    }

    if (forecastTurbines.length === 0) {
      errors.push('No turbines match the current forecast filters');
    }

    if (forecastTurbines.length < FORECAST_CALCULATION_CONSTANTS.MIN_FORECAST_POPULATION && forecastTurbines.length > 0) {
      errors.push(`Forecast subset too small: ${forecastTurbines.length} turbines (minimum ${FORECAST_CALCULATION_CONSTANTS.MIN_FORECAST_POPULATION} recommended)`);
    }

    if (!sourceAnalysis.parameters || !sourceAnalysis.parameters.shape || !sourceAnalysis.parameters.scale) {
      errors.push('Invalid Weibull parameters in source analysis');
    }

    return {
      populationSize: initialPopulationSize,
      forecastSubsetSize: forecastTurbines.length,
      turbinesForForecast: forecastTurbines,
      validationErrors: errors
    };
  }, [turbineData, populationTurbineData, sourceAnalysis, filters]);
};